import React from 'react';
import { Controller } from 'react-hook-form';

import { productCardDefaultViews, productCardTextAlignments } from 'shared/constants/customization';

import { ActiveSwitch } from 'src/components/active-switch';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';
import { OptionPicker } from '../components/option-picker';

import { useApplyToAllLocations } from '../use-apply-to-all-locations';
import { useProductCardsForm } from './use-product-cards-form';
import { ProductCardsFormProps } from './product-card.types';

import { Heading, HelperText, Input, Inputs, SubText } from '../customize.styles';
import { ProductCardPreview } from './product-card-preview';

export function ProductCardsForm(props: ProductCardsFormProps): JSX.Element {
  const sectionName = 'Product Cards';
  const {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    applyAllInput,
    afterApplyAll,
  } = useProductCardsForm(props);
  const { handleApplyToLocations, hasAccessToAllLocations, chainId, totalLocationCount } = useApplyToAllLocations({
    input: applyAllInput,
    afterApplyAll,
    sectionName,
  });

  return (
    <MenuCustomizationLayout
      headerText={sectionName}
      subHeaderText='Select how product cards will appear.'
      isDirty={isDirty}
      handlePublish={() => void handlePublish()}
      handleReset={handleReset}
      previewComponent={<ProductCardPreview data={previewData} />}
      handleApplyToLocations={handleApplyToLocations}
      hasAccessToAllLocations={hasAccessToAllLocations}
      chainId={chainId}
      totalLocationCount={totalLocationCount}
    >
      <Heading>Default View</Heading>
      <SubText>
        Select which product card type will display by default on category pages, search results and more.
      </SubText>

      <Inputs>
        <Input>
          <Controller
            name='defaultViewLarge'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker
                label='Large Screens'
                options={productCardDefaultViews}
                selectedOption={value}
                onChange={onChange}
              />
            )}
          />
          <HelperText>Includes desktop computers, laptops and tablets.</HelperText>
        </Input>

        <Input>
          <Controller
            name='defaultViewSmall'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker
                label='Small Screens'
                options={productCardDefaultViews}
                selectedOption={value}
                onChange={onChange}
              />
            )}
          />
          <HelperText>Includes mobile devices and smaller tablets.</HelperText>
        </Input>
      </Inputs>

      <Heading>Display Product Weight</Heading>
      <SubText>Display the product weight next to price on card view.</SubText>

      <Inputs>
        <Input>
          <Controller
            name='showProductWeight'
            control={control}
            render={({ onChange, value }) => (
              <ActiveSwitch name='showProductWeight' active={value} onClick={onChange} hasLabel />
            )}
          />
        </Input>
      </Inputs>

      <Heading>Full Width Images</Heading>
      <SubText>Display product images edge-to-edge on card view.</SubText>

      <Inputs>
        <Input>
          <Controller
            name='fullWidthImages'
            control={control}
            render={({ onChange, value }) => (
              <ActiveSwitch name='fullWidthImages' active={value} onClick={onChange} hasLabel />
            )}
          />
        </Input>
      </Inputs>

      <Heading>Text Alignment</Heading>
      <SubText>Select text alignment for card view</SubText>

      <Inputs>
        <Input>
          <Controller
            name='textAlignment'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker
                label='Text Alignment'
                options={productCardTextAlignments}
                selectedOption={value}
                onChange={onChange}
              />
            )}
          />
        </Input>
      </Inputs>
    </MenuCustomizationLayout>
  );
}
